import { useState } from "react";
import { message, Spin } from "antd";
import { WinnerStatsCards } from "./WinnerStatsCard";
import { WinnerCard } from "./WinnerCard";
import { EmailDialog } from "./EmailDialog";
import { WinnerDetail } from "./WinnerDetail";
import useContestService from "../../../../services/contestService";
import { giveAwayContent } from "../../../../utils/giveaAwayContent";
import { useLanguage } from "../../../../context/LanguageContext";

// components/winners/WinnerTab.jsx
export function WinnerTab({
  winners,
  setWinners,
  loading = false,
  onRefresh,
  onUpdateStatus,
  contestService,
}) {
  const { language } = useLanguage();
  const t = giveAwayContent[language].winnerTab;

  const [selectedWinner, setSelectedWinner] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState("");
  const [isEmailOpen, setIsEmailOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [actionLoading, setActionLoading] = useState(false);
  const { sendWinnerEmail } = useContestService();

  // Helper function to get normalized status
  const getWinnerStatus = (winner) => {
    return winner.notificationStatus || winner.status || "pending";
  };

  // Helper function to get normalized winner name
  const getWinnerName = (winner) => {
    return (
      `${winner.winner?.firstname || ""} ${
        winner.winner?.lastname || ""
      }`.trim() || t.unknownName
    );
  };

  // ===== COMPUTED VALUES =====
  const filteredWinners = winners.filter(
    (winner) =>
      filterStatus === "all" || getWinnerStatus(winner) === filterStatus
  );

  const winnerCounts = {
    all: winners.length,
    pending: winners.filter((w) => getWinnerStatus(w) === "pending").length,
    contacted: winners.filter((w) => getWinnerStatus(w) === "contacted").length,
    voucher_sent: winners.filter((w) => getWinnerStatus(w) === "voucher_sent")
      .length,
    completed: winners.filter((w) => getWinnerStatus(w) === "completed").length,
  };

  // ===== EMAIL HANDLING =====
  const handleSendEmail = (winner) => {
    setSelectedWinner(winner);

    // Normalisierte Daten für E-Mail Template
    const winnerName = getWinnerName(winner);
    const giveawayTitle = winner.giveawayTitle || t.unknownGiveaway;
    const prizeTitle = winner.prize?.title || t.unknownPrize;
    const prizeDescription = winner.prize?.description || "";
    const prizeValue = winner.prize?.value || t.unknownValue;
    const voucherCode = winner.prize?.voucherCode || "";

    // Email Template generieren mit lokalisierten Texten
    const template = t.emailTemplates.winner(
      winnerName,
      giveawayTitle,
      prizeTitle,
      prizeDescription,
      prizeValue,
      voucherCode
    );

    setEmailTemplate(template);
    setIsEmailOpen(true);
  };

  const handleSendVoucher = async () => {
    if (!selectedWinner) return;

    setActionLoading(true);
    try {
      // 1. E-Mail über den Contest Service senden
      await sendWinnerEmail(selectedWinner._id, emailTemplate);

      // 2. Winner Status auf "voucher_sent" aktualisieren
      await onUpdateStatus(selectedWinner._id, "voucher_sent");

      // 3. Lokalen State auch aktualisieren für sofortiges UI-Feedback
      setWinners((prev) =>
        prev.map((w) =>
          w._id === selectedWinner._id
            ? {
                ...w,
                voucherSent: true,
                voucherSentAt: new Date(),
                notificationStatus: "voucher_sent",
                contactAttempts: (w.contactAttempts || 0) + 1,
                lastEmailSent: new Date(),
              }
            : w
        )
      );

      setIsEmailOpen(false);
      message.success(t.success.voucherSent(getWinnerName(selectedWinner)));
    } catch (error) {
      console.error(t.errors.sendEmail, error);
      message.error(t.errors.sendEmailGeneric(error.message));
    } finally {
      setActionLoading(false);
    }
  };

  // Separate Funktion nur für E-Mail versenden (ohne Status-Update)
  const handleSendEmailOnly = async (customMessage = "") => {
    if (!selectedWinner) return;

    setActionLoading(true);
    try {
      // Nur E-Mail senden, ohne Status zu ändern
      await sendWinnerEmail(selectedWinner._id, customMessage || emailTemplate);

      // Lokalen State für E-Mail-Tracking aktualisieren
      setWinners((prev) =>
        prev.map((w) =>
          w._id === selectedWinner._id
            ? {
                ...w,
                lastEmailSent: new Date(),
                contactAttempts: (w.contactAttempts || 0) + 1,
              }
            : w
        )
      );

      message.success(t.success.emailSent(getWinnerName(selectedWinner)));
    } catch (error) {
      console.error(t.errors.sendEmail, error);
      message.error(t.errors.sendEmailGeneric(error.message));
    } finally {
      setActionLoading(false);
    }
  };

  // ===== STATUS HANDLING =====
  const handleStatusChange = async (winnerId, newStatus) => {
    setActionLoading(true);
    try {
      // API-Aufruf
      await onUpdateStatus(winnerId, newStatus);

      // Lokalen State aktualisieren
      setWinners((prev) =>
        prev.map((w) =>
          w._id === winnerId
            ? {
                ...w,
                notificationStatus: newStatus,
                contactAttempts:
                  newStatus === "contacted"
                    ? (w.contactAttempts || 0) + 1
                    : w.contactAttempts || 0,
              }
            : w
        )
      );

      message.success(t.success.statusUpdated);
    } catch (error) {
      console.error(t.errors.updateStatus, error);
      message.error(t.errors.updateStatusGeneric);
    } finally {
      setActionLoading(false);
    }
  };

  // ===== NOTES HANDLING =====
  const handleNotesUpdate = async (winnerId, notes) => {
    try {
      // Hier könnte man eine separate API für Notes haben
      // Für jetzt nur lokaler State
      setWinners((prev) =>
        prev.map((w) => (w._id === winnerId ? { ...w, notes } : w))
      );

      message.success(t.success.notesUpdated);
    } catch (error) {
      console.error(t.errors.updateNotes, error);
      message.error(t.errors.updateNotesGeneric);
    }
  };

  // ===== BULK ACTIONS =====
  const handleBulkStatusUpdate = async (status) => {
    const pendingWinners = filteredWinners.filter(
      (w) => getWinnerStatus(w) === "pending"
    );

    if (pendingWinners.length === 0) {
      message.warning(t.warnings.noPendingWinners);
      return;
    }

    setActionLoading(true);
    try {
      // Alle ausstehenden Winner in einem Batch aktualisieren
      const promises = pendingWinners.map((winner) =>
        onUpdateStatus(winner._id, status)
      );

      await Promise.all(promises);

      // Daten neu laden
      onRefresh();

      message.success(
        t.success.bulkStatusUpdate(pendingWinners.length, status)
      );
    } catch (error) {
      console.error(t.errors.bulkUpdate, error);
      message.error(t.errors.bulkUpdateGeneric);
    } finally {
      setActionLoading(false);
    }
  };

  // Bulk E-Mail Versendung
  const handleBulkSendEmail = async () => {
    const pendingWinners = filteredWinners.filter(
      (w) => getWinnerStatus(w) === "pending"
    );

    if (pendingWinners.length === 0) {
      message.warning(t.warnings.noPendingForEmail);
      return;
    }

    setActionLoading(true);
    try {
      // E-Mails an alle ausstehenden Winner senden
      const emailPromises = pendingWinners.map(async (winner) => {
        const winnerName = getWinnerName(winner);
        const template = t.emailTemplates.bulk(winnerName);
        return sendWinnerEmail(winner._id, template);
      });

      await Promise.all(emailPromises);

      // Status aller auf "contacted" setzen
      const statusPromises = pendingWinners.map((winner) =>
        onUpdateStatus(winner._id, "contacted")
      );

      await Promise.all(statusPromises);

      // Daten neu laden
      onRefresh();

      message.success(t.success.bulkEmailsSent(pendingWinners.length));
    } catch (error) {
      console.error(t.errors.bulkEmail, error);
      message.error(t.errors.bulkEmailGeneric);
    } finally {
      setActionLoading(false);
    }
  };

  // Helper für lokalisierte Filter-Status-Labels
  const getLocalizedFilterStatus = (status) => {
    return t.filterSelect[status] || status;
  };

  // ===== RENDER =====
  return (
    <div className="p-6">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-6 space-y-4">
          {/* Title Section */}
          <div className="text-center sm:text-left">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              {t.title}
            </h1>
            <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600">
              {t.subtitle}
            </p>
          </div>

          {/* Actions - Mobile Stack */}
          <div className="flex flex-col gap-3">
            {/* Top row - Always visible actions */}
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={onRefresh}
                disabled={loading || actionLoading}
                className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-orange-100 text-orange-600 rounded-lg hover:bg-orange-200 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
              >
                {loading ? (
                  <Spin size="small" />
                ) : (
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                )}
                <span>{t.refresh}</span>
              </button>
            </div>

            {/* Bulk Actions - Conditional */}
            {winnerCounts.pending > 0 && (
              <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                <button
                  onClick={handleBulkSendEmail}
                  disabled={loading || actionLoading}
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 font-medium text-center"
                >
                  <span className="block sm:inline">
                    {t.sendEmails} ({winnerCounts.pending})
                  </span>
                </button>
                <button
                  onClick={() => handleBulkStatusUpdate("contacted")}
                  disabled={loading || actionLoading}
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 font-medium text-center"
                >
                  <span className="block sm:inline">
                    {t.markAllContacted} ({winnerCounts.pending})
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <WinnerStatsCards winners={winners} language={language} />

        {/* Filter & Search */}
        <div className="mb-6 bg-white p-4 rounded-lg shadow-sm">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            {/* Status Filter Buttons */}
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setFilterStatus("all")}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterStatus === "all"
                    ? "bg-orange-500 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {t.filterButtons.all} ({winnerCounts.all})
              </button>
              <button
                onClick={() => setFilterStatus("pending")}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterStatus === "pending"
                    ? "bg-orange-500 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {t.filterButtons.pending} ({winnerCounts.pending})
                {winnerCounts.pending > 0 && (
                  <span className="ml-2 px-2 py-1 bg-red-500 text-white text-xs rounded-full">
                    !
                  </span>
                )}
              </button>
              <button
                onClick={() => setFilterStatus("contacted")}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterStatus === "contacted"
                    ? "bg-orange-500 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {t.filterButtons.contacted} ({winnerCounts.contacted})
              </button>
              <button
                onClick={() => setFilterStatus("voucher_sent")}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterStatus === "voucher_sent"
                    ? "bg-orange-500 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {t.filterButtons.voucherSent} ({winnerCounts.voucher_sent})
              </button>
              <button
                onClick={() => setFilterStatus("completed")}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  filterStatus === "completed"
                    ? "bg-orange-500 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                {t.filterButtons.completed} ({winnerCounts.completed})
              </button>
            </div>

            {/* Legacy Select */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 bg-white"
            >
              <option value="all">
                {t.filterSelect.showAll} ({winnerCounts.all})
              </option>
              <option value="pending">
                {t.filterSelect.pending} ({winnerCounts.pending})
              </option>
              <option value="contacted">
                {t.filterSelect.contacted} ({winnerCounts.contacted})
              </option>
              <option value="voucher_sent">
                {t.filterSelect.voucherSent} ({winnerCounts.voucher_sent})
              </option>
              <option value="completed">
                {t.filterSelect.completed} ({winnerCounts.completed})
              </option>
            </select>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="text-center">
              <Spin size="large" />
              <p className="mt-4 text-gray-600">{t.loadingWinners}</p>
            </div>
          </div>
        ) : (
          <>
            {/* Winners Grid */}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredWinners.map((winner) => (
                <WinnerCard
                  key={winner._id}
                  winner={winner}
                  onSendEmail={handleSendEmail}
                  onShowDetails={(winner) => {
                    setSelectedWinner(winner);
                    setIsDetailOpen(true);
                  }}
                  onStatusChange={handleStatusChange}
                  loading={actionLoading}
                  language={language} // ✅ Language prop durchreichen
                />
              ))}
            </div>

            {/* Empty State */}
            {filteredWinners.length === 0 && (
              <div className="text-center py-12">
                <div className="text-gray-400 text-4xl mb-4">
                  {filterStatus === "all" ? "🎁" : "⚠️"}
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {t.emptyState.noWinners}
                </h3>
                <p className="text-gray-500 mb-4">
                  {filterStatus === "all"
                    ? t.emptyState.noWinnersAll
                    : t.emptyState.noWinnersFiltered(
                        getLocalizedFilterStatus(filterStatus)
                      )}
                </p>

                {filterStatus !== "all" && (
                  <button
                    onClick={() => setFilterStatus("all")}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                  >
                    {t.showAllWinners}
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {/* Action Loading Overlay */}
        {actionLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg flex items-center space-x-3">
              <Spin size="large" />
              <span className="text-lg">{t.processing}</span>
            </div>
          </div>
        )}

        {/* Dialogs */}
        <EmailDialog
          open={isEmailOpen}
          onCancel={() => setIsEmailOpen(false)}
          onSend={handleSendVoucher}
          onSendEmailOnly={handleSendEmailOnly}
          winner={selectedWinner}
          emailTemplate={emailTemplate}
          setEmailTemplate={setEmailTemplate}
          loading={actionLoading}
          language={language} // ✅ Language prop durchreichen
        />

        <WinnerDetail
          open={isDetailOpen}
          onCancel={() => setIsDetailOpen(false)}
          winner={selectedWinner}
          onStatusChange={handleStatusChange}
          onNotesUpdate={handleNotesUpdate}
          onSendEmail={handleSendEmail}
          onSendEmailOnly={handleSendEmailOnly}
          loading={actionLoading}
          language={language} // ✅ Language prop durchreichen
        />
      </div>
    </div>
  );
}
